/**
 * Maps TenantPublicLaunch.status values to user-facing provisioning stages.
 * Progress percent comes from the API (progress_percent); stages are derived from status only.
 */

export const LAUNCH_STATUS_ORDER = [
  'pending',
  'provisioning',
  'schema_ready',
  'bootstrap_ready',
  'routing_ready',
  'dns_pending',
  'tls_pending',
  'delivery_pending',
  'activation_pending',
  'local_simulated_ready',
  'local_simulated_public_ready',
  'ready',
];

/** User-visible installation stages (ordered). Backend statuses map into these. */
export const LAUNCH_STAGE_DEFS = [
  {
    id: 'account',
    statuses: ['pending'],
  },
  {
    id: 'tenant',
    statuses: ['provisioning'],
  },
  {
    id: 'schema',
    statuses: ['schema_ready'],
  },
  {
    id: 'bootstrap',
    statuses: ['bootstrap_ready'],
  },
  {
    id: 'routing',
    statuses: ['routing_ready'],
  },
  {
    id: 'domain',
    statuses: ['dns_pending', 'tls_pending'],
  },
  {
    id: 'activation',
    statuses: ['delivery_pending', 'activation_pending'],
  },
  {
    id: 'ready',
    statuses: ['local_simulated_ready', 'local_simulated_public_ready', 'ready'],
  },
];

/**
 * Statuses that stop launch-status polling.
 * local_simulated_* is NOT terminal: H05H owned promotion may still flip the
 * launch to canonical ready + public_ready, and the SPA must observe that.
 *
 * Canonical ready + public_ready without public_url also keeps polling so Step 6
 * access hooks can appear once the derived public URL is present.
 */
export function isTerminalLaunchStatus(status, publicReady = undefined, publicUrl = undefined) {
  if (status === 'failed') {
    return true;
  }
  if (status !== 'ready') {
    return false;
  }
  // Legacy callers: status-only terminal check for ready.
  if (publicReady === undefined && publicUrl === undefined) {
    return true;
  }
  if (publicReady === true) {
    const url = typeof publicUrl === 'string' ? publicUrl.trim() : '';
    return url !== '';
  }
  return true;
}

/**
 * Local/E2E "success-looking" statuses for stage-list completion.
 * Must never alone gate Step 6 Done — use isCanonicalDoneLaunchStatus.
 */
export function isSuccessfulLaunchStatus(status) {
  return status === 'ready'
    || status === 'local_simulated_ready'
    || status === 'local_simulated_public_ready';
}

/**
 * Canonical production READY. local_simulated_* terminals are successful for
 * local/E2E runs but must never be presented as public production readiness.
 */
export function isCanonicalPublicReadyStatus(status) {
  return status === 'ready';
}

/**
 * Step 6 Done requires real canonical readiness, including public_ready.
 * local_simulated_public_ready must never satisfy this.
 */
export function isCanonicalDoneLaunchStatus(status, publicReady) {
  return isCanonicalPublicReadyStatus(status) && publicReady === true;
}

export function isLocalSimulatedLaunchStatus(status) {
  return status === 'local_simulated_ready'
    || status === 'local_simulated_public_ready';
}

/**
 * @param {string|null|undefined} status
 * @param {number|null|undefined} progressPercent
 * @returns {{ id: string, state: 'waiting'|'active'|'completed'|'failed' }[]}
 */
export function buildLaunchStageStates(status, progressPercent = null) {
  if (status === 'failed') {
    const pct = typeof progressPercent === 'number' ? progressPercent : 0;
    let failedAssigned = false;
    return LAUNCH_STAGE_DEFS.map((stage, index) => {
      if (stage.id === 'ready') {
        return { id: stage.id, state: 'waiting' };
      }
      const threshold = Math.round(((index + 1) / (LAUNCH_STAGE_DEFS.length - 1)) * 100);
      if (!failedAssigned && pct <= threshold) {
        failedAssigned = true;
        return { id: stage.id, state: 'failed' };
      }
      if (!failedAssigned) {
        return { id: stage.id, state: 'completed' };
      }
      return { id: stage.id, state: 'waiting' };
    });
  }

  const statusIndex = LAUNCH_STATUS_ORDER.indexOf(status || 'pending');
  const safeIndex = statusIndex < 0 ? 0 : statusIndex;

  return LAUNCH_STAGE_DEFS.map((stage) => {
    const stageStatusIndexes = stage.statuses
      .map((s) => LAUNCH_STATUS_ORDER.indexOf(s))
      .filter((i) => i >= 0);
    const stageMin = Math.min(...stageStatusIndexes);
    const stageMax = Math.max(...stageStatusIndexes);

    if (safeIndex > stageMax) {
      return { id: stage.id, state: 'completed' };
    }
    if (safeIndex >= stageMin && safeIndex <= stageMax) {
      if (stage.id === 'ready' && isSuccessfulLaunchStatus(status)) {
        return { id: stage.id, state: 'completed' };
      }
      return { id: stage.id, state: 'active' };
    }
    return { id: stage.id, state: 'waiting' };
  });
}

/** Mirrors TenantPublicLaunch::estimateProgress() when API omits progress_percent. */
const BACKEND_STATUS_PROGRESS = {
  pending: 5,
  provisioning: 15,
  schema_ready: 35,
  bootstrap_ready: 45,
  routing_ready: 50,
  dns_pending: 60,
  tls_pending: 75,
  delivery_pending: 85,
  activation_pending: 95,
  local_simulated_ready: 100,
  local_simulated_public_ready: 100,
  ready: 100,
  failed: 0,
};

export function resolveLaunchProgressPercent(statusPayload) {
  const raw = statusPayload?.progress_percent;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(0, Math.min(100, Math.round(raw)));
  }
  const status = statusPayload?.status || 'pending';
  if (Object.prototype.hasOwnProperty.call(BACKEND_STATUS_PROGRESS, status)) {
    return BACKEND_STATUS_PROGRESS[status];
  }
  return 0;
}
